import React, { useState, useEffect } from 'react';
import { 
  Tractor, Sun, Shield, Zap, Wifi, ArrowRight, Check, 
  Calculator, Phone, ChevronDown, Droplets, Battery, Thermometer,
  MapPin, Clock, Wrench, Mountain, TreePine, Milk
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const ServicioRuralPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const benefits = [
    {
      icon: Zap,
      title: 'Independencia Total de ANDE',
      description: 'Generá tu propia electricidad donde la red no llega. Olvidate de los cortes constantes y las líneas inestables.'
    },
    {
      icon: Battery,
      title: 'Sistemas con Baterías',
      description: 'Almacená energía para usarla de noche o durante días nublados. Funcionamiento 24/7 garantizado.'
    },
    {
      icon: Wifi,
      title: 'Internet Satelital Opcional',
      description: 'Agregá conectividad donde necesites (servicio adicional con costo aparte). Mantenete conectado desde cualquier punto.'
    },
    {
      icon: Droplets,
      title: 'Bombeo de Agua Solar',
      description: 'Sistemas de bombeo para pozos, tanques y riego sin costo eléctrico. Ideal para ganadería y agricultura.'
    },
    {
      icon: Shield,
      title: 'Equipos Resistentes',
      description: 'Componentes diseñados para soportar las condiciones del campo paraguayo: calor, lluvia y polvo.'
    },
    {
      icon: Wrench,
      title: 'Soporte en Todo el País',
      description: 'Equipo técnico con cobertura nacional. Llegamos a donde otros no llegan.'
    }
  ];

  const applications = [
    {
      icon: Tractor,
      title: 'Estancias Ganaderas',
      description: 'Energía para cascos, galpones, sistemas de agua y cercos eléctricos',
      uses: ['Iluminación general', 'Bombeo de agua', 'Cercos eléctricos', 'Refrigeración']
    },
    {
      icon: TreePine,
      title: 'Fincas Agrícolas',
      description: 'Sistemas para riego, procesamiento y almacenamiento de cosechas',
      uses: ['Sistemas de riego', 'Secadores solares', 'Refrigeración', 'Maquinaria básica']
    },
    {
      icon: Milk,
      title: 'Tambos y Lecherías',
      description: 'Energía confiable para ordeñadoras y tanques de frío',
      uses: ['Ordeñadoras eléctricas', 'Tanques de frío', 'Iluminación', 'Bombeo de agua']
    },
    {
      icon: Mountain,
      title: 'Cabañas y Retiros',
      description: 'Soluciones para propiedades recreativas alejadas de la red',
      uses: ['Confort básico', 'Iluminación', 'Electrodomésticos', 'Conectividad']
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Visita Técnica',
      description: 'Visitamos tu propiedad para evaluar las necesidades y el mejor diseño del sistema.'
    },
    {
      number: '02',
      title: 'Diseño a Medida',
      description: 'Creamos una solución específica para tus requerimientos de energía y presupuesto.'
    },
    {
      number: '03',
      title: 'Presupuesto Claro',
      description: 'Te presentamos opciones con costos detallados y formas de pago accesibles.'
    },
    {
      number: '04',
      title: 'Instalación',
      description: 'Nuestro equipo se traslada a tu propiedad y realiza la instalación completa.'
    },
    {
      number: '05',
      title: 'Capacitación',
      description: 'Te enseñamos a usar y mantener tu sistema. Soporte continuo garantizado.'
    }
  ];

  const packages = [
    {
      name: 'Kit Rural Básico',
      power: '1-2 kW',
      description: 'Iluminación y cargas básicas',
      includes: ['4-6 paneles solares', 'Inversor/cargador', 'Baterías 24h autonomía', 'Estructura de montaje'],
      idealFor: 'Puestos de estancia, casetas de vigilancia',
      highlighted: false
    },
    {
      name: 'Kit Rural Confort',
      power: '3-5 kW',
      description: 'Hogar completo off-grid',
      includes: ['8-12 paneles solares', 'Inversor híbrido', 'Baterías 48h autonomía', 'Bomba de agua solar', 'Sistema de monitoreo'],
      idealFor: 'Cascos de estancia, fincas medianas',
      highlighted: true
    },
    {
      name: 'Kit Rural Industrial',
      power: '10+ kW',
      description: 'Operaciones productivas',
      includes: ['20+ paneles solares', 'Inversor industrial', 'Banco de baterías', 'Bombeo de alto caudal', 'Respaldo diesel opcional'],
      idealFor: 'Tambos, operaciones agrícolas intensivas',
      highlighted: false
    }
  ];

  const faqs = [
    {
      question: '¿Funciona el sistema en días nublados o de noche?',
      answer: 'Sí. Nuestros sistemas incluyen baterías que almacenan energía durante el día para usarla de noche o en días con poca luz solar. Dimensionamos las baterías según tus necesidades para garantizar autonomía de 24 a 72 horas.'
    },
    {
      question: '¿Puedo agregar internet satelital a mi sistema?',
      answer: 'Sí, ofrecemos internet satelital como servicio opcional con costo adicional. Es ideal para zonas sin cobertura de fibra o señal celular. El consumo del equipo de internet se incluye en el dimensionamiento de tu sistema solar.'
    },
    {
      question: '¿Qué mantenimiento requiere el sistema en el campo?',
      answer: 'El mantenimiento es mínimo: limpieza ocasional de paneles (especialmente en época seca), revisión de baterías cada 6 meses y una inspección técnica anual. Ofrecemos planes de mantenimiento con visitas programadas.'
    },
    {
      question: '¿Pueden instalar en cualquier parte del país?',
      answer: 'Sí, tenemos cobertura nacional. Hemos instalado sistemas en el Chaco, zonas rurales del Este, y propiedades alejadas en todo el país. Coordinamos la logística para llegar a donde necesites.'
    },
    {
      question: '¿Cuánto dura la instalación en zonas rurales?',
      answer: 'Una instalación típica rural toma entre 2 y 5 días dependiendo del tamaño del sistema y la accesibilidad del lugar. Nuestro equipo lleva todo el material necesario y trabaja de forma autónoma.'
    },
    {
      question: '¿Tienen financiamiento para productores rurales?',
      answer: 'Sí, trabajamos con líneas de crédito especiales para el sector agropecuario, incluyendo opciones del BNF y cooperativas. También ofrecemos financiamiento propio con condiciones flexibles.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden" data-testid="rural-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-20 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-[#90EE90] rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
                <Tractor className="w-4 h-4 text-[#90EE90]" />
                <span className="text-sm font-medium text-[#90EE90]">Soluciones Rurales y Agro</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Energía Confiable para el
                <span className="text-[#90EE90]"> Campo Paraguayo</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
                Olvidate de los cortes de luz y del alto costo de la ANDE. Llevamos energía solar 
                a las zonas más alejadas con opciones de conectividad satelital disponibles.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/calculadora')}
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
                  data-testid="cta-calculator"
                >
                  <Calculator size={20} />
                  Calcular mi Sistema
                </button>
                <a
                  href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20energía%20solar%20para%20mi%20propiedad%20rural"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
                  data-testid="cta-whatsapp"
                >
                  <Phone size={20} />
                  Contactar Ahora
                </a>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800"
                  alt="Campo con energía solar"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#90EE90]/20 rounded-full">
                    <MapPin className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-zinc-900">Cobertura Nacional</div>
                    <div className="text-sm text-zinc-500">Llegamos a todo el país</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 md:py-28 bg-zinc-50" data-testid="applications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Aplicaciones
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Soluciones para el Campo
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Energía solar adaptada a cada tipo de propiedad rural
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((app, index) => {
              const Icon = app.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-2xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`application-card-${index}`}
                >
                  <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-4">
                    <Icon className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{app.title}</h3>
                  <p className="text-sm text-zinc-600 mb-4">{app.description}</p>
                  <ul className="space-y-1">
                    {app.uses.map((use, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-zinc-500">
                        <div className="w-1 h-1 bg-[#90EE90] rounded-full"></div>
                        {use}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              ¿Por qué elegirnos?
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Ventajas para el Productor Rural
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Diseñamos sistemas pensados para las necesidades reales del campo
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="bg-zinc-50 p-8 rounded-2xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`benefit-card-${index}`}
                >
                  <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-6">
                    <Icon className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-3">{benefit.title}</h3>
                  <p className="text-zinc-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>

          {/* Internet Notice */}
          <div className="mt-12 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Wifi className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-amber-900 mb-1">Sobre el Internet Satelital</h4>
                <p className="text-amber-800 text-sm">
                  El servicio de internet satelital es <strong>opcional y con costo adicional</strong>. 
                  No está incluido por defecto en los kits de energía solar. Consultanos para conocer 
                  las opciones disponibles y costos según tu ubicación.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-zinc-900" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Nuestro Proceso
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Cómo Trabajamos?
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Un proceso simple adaptado a las realidades del campo
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className="text-center p-6 bg-zinc-800 rounded-2xl border border-zinc-700"
              >
                <div className="mx-auto w-14 h-14 bg-[#90EE90] rounded-full flex items-center justify-center mb-4">
                  <span className="text-lg font-bold text-zinc-900">{step.number}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-zinc-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 md:py-28 bg-zinc-50" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Kits Rurales
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Soluciones Listas para Instalar
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Sistemas completos diseñados para el campo paraguayo
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 ${
                  pkg.highlighted 
                    ? 'bg-gradient-to-b from-[#90EE90]/20 to-white border-2 border-[#90EE90] shadow-xl' 
                    : 'bg-white border border-zinc-200'
                }`}
                data-testid={`package-card-${index}`}
              >
                {pkg.highlighted && (
                  <div className="absolute top-0 left-0 right-0 bg-[#90EE90] text-zinc-900 text-center py-2 text-sm font-bold">
                    MÁS POPULAR
                  </div>
                )}
                
                <div className={`p-8 ${pkg.highlighted ? 'pt-14' : ''}`}>
                  <h3 className="text-2xl font-bold text-zinc-900 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-[#14532D] mb-2">{pkg.power}</div>
                  <p className="text-zinc-500 mb-6">{pkg.description}</p>
                  
                  <div className="bg-zinc-100 rounded-lg p-4 mb-6">
                    <p className="text-xs font-semibold uppercase text-zinc-500 mb-2">Ideal para</p>
                    <p className="text-sm text-zinc-700">{pkg.idealFor}</p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {pkg.includes.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-zinc-700">
                        <Check className="w-5 h-5 text-[#90EE90] flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => navigate('/calculadora')}
                    className={`w-full py-4 rounded-xl font-bold transition-all duration-300 ${
                      pkg.highlighted
                        ? 'bg-[#14532D] text-white hover:bg-[#166534]'
                        : 'bg-zinc-200 text-zinc-900 hover:bg-zinc-300'
                    }`}
                  >
                    Solicitar Cotización
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-zinc-500 mt-8 text-sm">
            * Los kits son referenciales. Cada sistema se personaliza según tus necesidades específicas.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-zinc-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Dudas sobre Energía Rural
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden bg-white"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-zinc-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-[#14532D] to-[#166534]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
            <Tractor className="w-4 h-4 text-[#90EE90]" />
            <span className="text-sm font-medium text-white">Para el Campo</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Llevá Energía a Tu Propiedad Rural
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Consultanos sin compromiso. Visitamos tu propiedad y te asesoramos sobre la mejor solución.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/calculadora')}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
            >
              <Calculator size={20} />
              Calcular Sistema
            </button>
            <a
              href="https://wa.me/595991344956?text=Hola%20Zenex!%20Tengo%20una%20propiedad%20rural%20y%20me%20interesa%20energía%20solar"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-8 py-4 bg-white text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:bg-zinc-100"
            >
              <Phone size={20} />
              Solicitar Visita Técnica
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default ServicioRuralPage;
